import { supabase } from './supabaseService';
import { resendService } from './resendService';
import type { Proposal, ProposalContent, ProposalEvent } from '../types/proposal-content';

export interface ProposalListItem {
  id: string;
  title: string;
  status: Proposal['status'];
  public_slug: string;
  client_id: string | null;
  client_name?: string;
  client_email?: string;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  first_viewed_at: string | null;
  accepted_at: string | null;
  valid_until: string | null;
}

export interface CreateProposalInput {
  title: string;
  client_id?: string;
  template_id?: string;
  content: ProposalContent;
  valid_until?: string;
}

export interface UpdateProposalInput {
  title?: string;
  client_id?: string;
  content?: ProposalContent;
  valid_until?: string;
  status?: Proposal['status'];
}

class ProposalService {
  async getProposals(statusFilter?: Proposal['status']): Promise<ProposalListItem[]> {
    let query = supabase
      .from('proposals')
      .select(`
        id, title, status, public_slug, client_id,
        created_at, updated_at, sent_at, first_viewed_at, accepted_at, valid_until,
        contacts (full_name, email)
      `)
      .order('updated_at', { ascending: false });

    if (statusFilter) {
      query = query.eq('status', statusFilter);
    }

    const { data, error } = await query;
    if (error) throw new Error(`Error cargando propuestas: ${error.message}`);

    return (data || []).map((row: any) => ({
      id: row.id,
      title: row.title,
      status: row.status,
      public_slug: row.public_slug,
      client_id: row.client_id,
      client_name: row.contacts?.full_name || null,
      client_email: row.contacts?.email || null,
      created_at: row.created_at,
      updated_at: row.updated_at,
      sent_at: row.sent_at,
      first_viewed_at: row.first_viewed_at,
      accepted_at: row.accepted_at,
      valid_until: row.valid_until,
    }));
  }

  async getProposal(id: string): Promise<Proposal> {
    const { data, error } = await supabase
      .from('proposals')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw new Error(`Error cargando propuesta: ${error.message}`);
    return data as Proposal;
  }

  async createProposal(input: CreateProposalInput): Promise<Proposal> {
    const { data: user } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('proposals')
      .insert({
        title: input.title,
        client_id: input.client_id || null,
        template_id: input.template_id || null,
        content: input.content,
        valid_until: input.valid_until || null,
        created_by: user.user?.id || null,
        status: 'draft',
      })
      .select()
      .single();

    if (error) throw new Error(`Error creando propuesta: ${error.message}`);

    // Insert creation event
    await supabase.from('proposal_events').insert({
      proposal_id: data.id,
      event_type: 'created',
    });

    return data as Proposal;
  }

  async updateProposal(id: string, input: UpdateProposalInput): Promise<Proposal> {
    const updateData: Record<string, any> = {};
    if (input.title !== undefined) updateData.title = input.title;
    if (input.client_id !== undefined) updateData.client_id = input.client_id;
    if (input.content !== undefined) updateData.content = input.content;
    if (input.valid_until !== undefined) updateData.valid_until = input.valid_until;
    if (input.status !== undefined) updateData.status = input.status;

    const { data, error } = await supabase
      .from('proposals')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(`Error actualizando propuesta: ${error.message}`);
    return data as Proposal;
  }

  async deleteProposal(id: string): Promise<void> {
    const { error } = await supabase
      .from('proposals')
      .delete()
      .eq('id', id);

    if (error) throw new Error(`Error eliminando propuesta: ${error.message}`);
  }

  async duplicateProposal(id: string): Promise<Proposal> {
    const original = await this.getProposal(id);
    return this.createProposal({
      title: `${original.title} (copia)`,
      client_id: original.client_id || undefined,
      content: original.content,
    });
  }

  async sendProposal(id: string, recipientEmail?: string): Promise<void> {
    // Get proposal with client info
    const { data: proposal, error } = await supabase
      .from('proposals')
      .select('*, contacts (full_name, email)')
      .eq('id', id)
      .single();

    if (error || !proposal) throw new Error('Propuesta no encontrada');
    if (proposal.status !== 'draft') throw new Error('Solo se pueden enviar propuestas en borrador');

    const clientEmail = recipientEmail || (proposal.contacts as any)?.email;
    const clientName = (proposal.contacts as any)?.full_name || 'Cliente';

    if (!clientEmail) throw new Error('No hay email de destinatario. Asigna un cliente o indica un email.');

    // Update status
    await supabase
      .from('proposals')
      .update({ status: 'sent', sent_at: new Date().toISOString() })
      .eq('id', id);

    // Build public URL
    const publicUrl = `${window.location.origin}/propuestas/${proposal.public_slug}`;

    // Load config from DB (antes usaba resendService.loadConfig() en
    // localStorage, que falla en incógnito o dispositivo nuevo).
    const config = await resendService.loadConfigFromDatabase();
    if (!config?.isConfigured || !config.apiKey) {
      throw new Error('Resend no está configurado. Ve a Configuración > Email.');
    }

    await resendService.sendEmail({
      from: `${config.fromName || 'Cero Ideas'} <${config.fromEmail || 'hola@ceroideas.es'}>`,
      to: clientEmail,
      subject: `Nueva propuesta: ${proposal.title}`,
      html: this.buildProposalEmailHtml(proposal.title, clientName, publicUrl, proposal.content?.projectName),
      tags: [
        { name: 'type', value: 'proposal' },
        { name: 'proposal_id', value: id },
      ],
    });

    // Insert sent event
    await supabase.from('proposal_events').insert({
      proposal_id: id,
      event_type: 'sent',
      metadata: { recipient_email: clientEmail },
    });
  }

  async getProposalEvents(proposalId: string): Promise<ProposalEvent[]> {
    const { data, error } = await supabase
      .from('proposal_events')
      .select('*')
      .eq('proposal_id', proposalId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Error cargando eventos: ${error.message}`);
    return (data || []) as ProposalEvent[];
  }

  getPublicUrl(slug: string): string {
    return `${window.location.origin}/propuestas/${slug}`;
  }

  private buildProposalEmailHtml(
    title: string,
    clientName: string,
    publicUrl: string,
    projectName?: string
  ): string {
    return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#0A0A0A;font-family:'Inter',system-ui,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#0A0A0A;padding:40px 20px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#111111;border-radius:12px;border:1px solid rgba(255,255,255,0.08);overflow:hidden;">
        <!-- Header -->
        <tr><td style="padding:32px 40px 24px;border-bottom:1px solid rgba(255,255,255,0.08);">
          <table width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td>
                <div style="display:inline-block;width:32px;height:32px;background:#B6FF3C;border-radius:6px;text-align:center;line-height:32px;font-weight:700;font-size:16px;color:#0A0A0A;">C</div>
                <span style="margin-left:10px;font-size:14px;font-weight:600;color:#F0F0F0;">Cero Ideas</span>
              </td>
            </tr>
          </table>
        </td></tr>
        <!-- Body -->
        <tr><td style="padding:40px;">
          <p style="font-size:14px;color:#A0A0A0;margin:0 0 8px;">Hola ${clientName},</p>
          <h1 style="font-size:24px;font-weight:700;color:#F0F0F0;margin:0 0 8px;letter-spacing:-0.02em;">${title}</h1>
          ${projectName ? `<p style="font-size:13px;color:#666666;font-family:'JetBrains Mono',monospace;margin:0 0 24px;">${projectName}</p>` : ''}
          <p style="font-size:14px;color:#A0A0A0;line-height:1.6;margin:0 0 32px;">
            Hemos preparado una propuesta para ti. Puedes revisarla completa en el siguiente enlace:
          </p>
          <!-- CTA Button -->
          <table cellpadding="0" cellspacing="0" style="margin:0 0 32px;">
            <tr><td style="background:#B6FF3C;border-radius:8px;">
              <a href="${publicUrl}" style="display:inline-block;padding:12px 28px;font-size:14px;font-weight:600;color:#0A0A0A;text-decoration:none;font-family:'Inter',system-ui,sans-serif;">
                Ver propuesta →
              </a>
            </td></tr>
          </table>
          <p style="font-size:12px;color:#666666;margin:0;word-break:break-all;">
            O copia este enlace: <a href="${publicUrl}" style="color:#B6FF3C;">${publicUrl}</a>
          </p>
        </td></tr>
        <!-- Footer -->
        <tr><td style="padding:24px 40px;border-top:1px solid rgba(255,255,255,0.08);">
          <p style="font-size:11px;color:#666666;margin:0;text-align:center;">
            Este email ha sido enviado desde Cero Ideas CRM. Si tienes dudas, responde directamente a este correo.
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
  }
}

export const proposalService = new ProposalService();
